import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { doc, deleteDoc, collection, getDocs, updateDoc, arrayUnion, arrayRemove, getDoc } from 'firebase/firestore';
import { 
  User, 
  Clock, 
  MessageSquare, 
  Heart,
  MoreHorizontal,
  AlertTriangle,
  FileText,
  Bookmark,
  Trash2,
  Edit,
  ShieldCheck
} from 'lucide-react';

export default function PostCard({ post }) {
  // All Hooks are called at the top level
  const { currentUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [commentCount, setCommentCount] = useState(0);
  const [likes, setLikes] = useState(post ? post.likes || [] : []);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (currentUser) {
      const adminRef = doc(db, 'admins', currentUser.uid);
      const checkAdmin = async () => {
        const adminSnap = await getDoc(adminRef);
        setIsAdmin(adminSnap.exists());
      };
      checkAdmin();
    } else {
      setIsAdmin(false);
    }
  }, [currentUser]);

  useEffect(() => {
    if (!post || !post.id) return;

    const postRef = doc(db, 'posts', post.id);
    const commentsCol = collection(postRef, 'comments');

    const fetchCounts = async () => {
      const [commentsSnap, postSnap] = await Promise.all([
        getDocs(commentsCol),
        getDoc(postRef),
      ]);
      setCommentCount(commentsSnap.size);
      if (postSnap.exists()) setLikes(postSnap.data().likes || []);
    };

    fetchCounts();
  }, [post]);

  // Safety check happens after all hooks
  if (!post) {
    return null;
  }

  const hasLiked = currentUser ? likes.includes(currentUser.uid) : false;
  const isCfe = post.type === 'CFE';
  const isPostPage = location.pathname.includes('/post/');
  
  const borderColor = post.verified ? 'border-green-500/40 hover:border-green-500/60' : (isCfe ? 'border-yellow-500/20 hover:border-yellow-500/40' : 'border-cyan-500/20 hover:border-cyan-500/40');
  const iconBgColor = isCfe ? 'from-yellow-500/20 to-orange-500/20' : 'from-cyan-500/20 to-blue-500/20';
  const iconColor = isCfe ? 'text-yellow-400' : 'text-cyan-400';
  const badgeBgColor = isCfe ? 'bg-yellow-500/20' : 'bg-cyan-500/20';
  const badgeTextColor = isCfe ? 'text-yellow-300' : 'text-cyan-300';
  
  const authorDisplay = post.authorEmail ? 
    `0x${post.authorEmail.substring(0, 2).toUpperCase()}...${post.authorEmail.substring(post.authorEmail.length - 3)}` : 
    'Anonymous';

  // Debug log to check if authorId exists
  if (!post.authorId) {
    console.warn('Post missing authorId:', post);
  }

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      try {
        await deleteDoc(doc(db, 'posts', post.id));
        if(isPostPage) {
           navigate('/');
        }
      } catch (error) {
        console.error("Error removing document: ", error);
      }
    }
  };

  const handleLike = async () => {
    if (!currentUser) {
        alert("Please log in to like posts.");
        return;
    }
    const postRef = doc(db, 'posts', post.id);
    if (hasLiked) {
      await updateDoc(postRef, { likes: arrayRemove(currentUser.uid) });
    } else {
      await updateDoc(postRef, { likes: arrayUnion(currentUser.uid) });
    }
  };
  
  const handleVerify = async () => {
    if (!isAdmin) return;
    const postRef = doc(db, 'posts', post.id);
    await updateDoc(postRef, { verified: true });
  };
  
  // The card is now a standard <article> tag, not a Link
  return (
    <article className={`glass-card rounded-xl p-6 border ${borderColor} transition-all group`}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${iconBgColor} flex items-center justify-center`}>
            {isCfe ? <AlertTriangle size={24} className={iconColor} /> : <FileText size={24} className={iconColor} />}
          </div>
          <div>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold ${badgeBgColor} ${badgeTextColor}`}>
              {isCfe ? `CFE REPORT` : 'BLOG POST'}
            </span>
            <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
              <div className="flex items-center gap-1">
                <User size={12} />
                {post.authorId ? (
                  <Link 
                    to={`/profile/${post.authorId}`} 
                    className="hover:text-cyan-400 transition-colors relative z-10 font-medium cursor-pointer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {authorDisplay}
                  </Link>
                ) : (
                  <span className="font-medium">{authorDisplay}</span>
                )}
              </div>
              <div className="flex items-center gap-1">
                <Clock size={12} />
                {post.createdAt ? new Date(post.createdAt.toDate()).toLocaleDateString() : 'Just now'}
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
           {isAdmin && !post.verified && (
            <button onClick={handleVerify} className="opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity glass-card p-2 rounded-lg hover:bg-green-500/20" title="Verify Post">
                <ShieldCheck size={18} className="text-green-400" />
            </button>
           )}
           {currentUser && currentUser.uid === post.authorId && (
            <>
              <Link to={`/edit-post/${post.id}`} className="opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity glass-card p-2 rounded-lg hover:bg-cyan-500/20">
                <Edit size={18} className="text-cyan-400" />
              </Link>
              <button onClick={handleDelete} className="opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity glass-card p-2 rounded-lg hover:bg-red-500/20">
                <Trash2 size={18} className="text-red-400" />
              </button>
            </>
          )}
          <button className="opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity glass-card p-2 rounded-lg hover:bg-white/5"><Bookmark size={18} className="text-gray-400" /></button>
          <button className="opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity glass-card p-2 rounded-lg hover:bg-white/5"><MoreHorizontal size={18} className="text-gray-400" /></button>
        </div>
      </div>
      
      {/* The title is now the main link to the post page */}
      <Link to={`/post/${post.id}`}>
        <h2 className={`text-xl font-bold text-white mb-3 hover:${isCfe ? 'text-yellow-400' : 'text-cyan-400'} transition-colors cursor-pointer`}>{post.title}</h2>
      </Link>
      
      {post.imageUrl && (
        <div className="my-4">
          <img src={post.imageUrl} alt="Evidence" className="w-full h-auto max-h-[400px] object-contain rounded-lg"/>
        </div>
      )}
      <p className={`text-gray-300 mb-4 whitespace-pre-wrap ${isPostPage ? '' : 'line-clamp-3'}`}>{post.content}</p>
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          {post.verified && (
            <div className="flex items-center gap-2 text-sm text-green-400 font-semibold">
                <ShieldCheck size={16} />
                <span>Verified Report</span>
            </div>
          )}
          {!isCfe && (<><span className="glass-card px-3 py-1 rounded-lg text-xs font-medium text-gray-300">Education</span><span className="glass-card px-3 py-1 rounded-lg text-xs font-medium text-gray-300">Security</span></>)}
        </div>
        <div className="flex items-center gap-4">
          <button onClick={handleLike} className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors">
            <Heart size={16} className={hasLiked ? 'text-red-500 fill-current' : ''} />
            <span>{likes.length}</span>
          </button>
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <MessageSquare size={16} />
            <span>{commentCount}</span>
          </div>
        </div>
      </div>
    </article>
  );
}